// Globals
import { TFunction } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';
import { Progress } from 'antd';

// Components
import { StyledBadge, StyledBadgeWrapper, StyledDownloadButton, StyledProgressCell } from './ImportsListStyles';
import { Link } from '@/ui';

// Models
import { ImportStatusEnum, IProductsImportItem } from '@/models/productsImport.model';

// Helpers
import { formatDate } from '@/utils';
import { getImportStatusLabel, getImportTypeLabel } from '../../helpers/dataRender';
import { DownloadIcon } from '@/icons';
import { ROUTES } from '@/models/router.model';

type TGetImportsTableColumns = {
  t: TFunction;
};

const PROGRESS_STATUS: Record<ImportStatusEnum, 'success' | 'exception' | 'active'> = {
  [ImportStatusEnum.PROCESSING]: 'active',
  [ImportStatusEnum.FINISHED]: 'success',
  [ImportStatusEnum.FAILED]: 'exception',
};

export const getImportsTableColumns = ({ t }: TGetImportsTableColumns): ColumnsType<IProductsImportItem> => [
  {
    title: t('imports_list_date'),
    dataIndex: 'created_at',
    key: 'created_at',
    render: (created_at) => <span>{created_at ? formatDate(created_at) : '-'}</span>,
  },
  {
    title: t('imports_list_type'),
    dataIndex: 'type',
    key: 'type',
    render: (type) => getImportTypeLabel(type, t),
  },
  {
    title: t('imports_list_filter_name'),
    dataIndex: 'filter_name',
    key: 'filter_name',
    render: (_, record) => {
      return (
        <>
          {record?.import_model?.id && record?.import_model.title ? (
            <Link path={`${ROUTES.CATEGORY_FILTERS}/${record?.import_model?.id}`}>{record?.import_model.title}</Link>
          ) : (
            <span>{'-'}</span>
          )}
        </>
      );
    },
  },
  {
    title: t('imports_list_partner'),
    dataIndex: 'partner',
    key: 'partner',
    render: (partner) => <span>{partner ?? '-'}</span>,
  },
  {
    title: t('imports_list_status'),
    dataIndex: 'status',
    key: 'status',
    render: (status) => (
      <StyledBadgeWrapper>
        <StyledBadge $status={status}>
          <div className="indicator" />
          <span>{getImportStatusLabel(status, t)}</span>
        </StyledBadge>
      </StyledBadgeWrapper>
    ),
  },
  {
    title: t('imports_list_progress'),
    key: 'progress',
    render: (_, record) => {
      const { status, progress_count: progress, total_count: total } = record;
      const percent = total > 0 ? Math.min(100, Math.round((progress / total) * 100)) : 0;

      return (
        <StyledProgressCell>
          <Progress
            percent={status === ImportStatusEnum.FINISHED ? 100 : percent}
            status={PROGRESS_STATUS[status]}
            showInfo={false}
            size="small"
          />
          <span>
            {progress ?? 0}
            {total ? ` / ${total}` : ''}
          </span>
        </StyledProgressCell>
      );
    },
  },
  {
    title: t('imports_list_total_count'),
    dataIndex: 'total_count',
    key: 'total_count',
    render: (total_count) => <span>{total_count ?? '-'}</span>,
  },
  {
    title: t('imports_list_error_count'),
    dataIndex: 'error_count',
    key: 'error_count',
    render: (error_count) => <span>{error_count ?? '-'}</span>,
  },
  {
    title: t('imports_list_error_file'),
    dataIndex: 'errors_url',
    key: 'errors_url',
    render: (errors_url) => {
      return errors_url ? (
        <StyledDownloadButton href={errors_url} rel="noreferrer">
          <DownloadIcon />
          {t('imports_list_download')}
        </StyledDownloadButton>
      ) : (
        ''
      );
    },
  },
];
